import axios from 'axios';
import { useAuthStore } from '../stores/auth.store';

// Base API client without auth
export const apiClient = axios.create({
  // check if lVITE_IS_LOCAL
  baseURL:
    import.meta.env.VITE_IS_LOCAL === 'true'
      ? import.meta.env.VITE_LOCAL_API_BASE_URL
      : import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const getToken = useAuthStore.getState().getToken;
      if (!getToken) {
        console.warn(
          '⚠️ API Request without auth: getToken not initialized yet',
          {
            url: config.url,
            method: config.method,
          }
        );
        return config; // Continue without auth header
      }

      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ Added auth token to request:', config.url);
      } else {
        console.warn('⚠️ API Request: Auth0 token is empty', {
          url: config.url,
          method: config.method,
        });
      }
    } catch (error) {
      console.warn('❌ Failed to get Auth0 token for request:', {
        url: config.url,
        method: config.method,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor with automatic token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized responses
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.warn('🔄 401 detected, attempting token refresh...');
      originalRequest._retry = true; // Prevent infinite retry loops

      try {
        const { getTokenSilently } = useAuthStore.getState();

        if (getTokenSilently) {
          // Force refresh the token (bypass cache)
          const newToken = await getTokenSilently({ cacheMode: 'off' });

          if (newToken) {
            console.log('✅ Token refreshed successfully, retrying request');
            // Update the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            // Retry the original request
            return apiClient(originalRequest);
          }
        }

        // If we get here, token refresh failed
        console.error('❌ Token refresh failed, clearing auth state');
        throw new Error('Token refresh failed');
      } catch (refreshError) {
        console.error('❌ Auth session expired, calling Auth0 logout...');

        // Get the logout function from the store
        const { logout } = useAuthStore.getState();
        // Clear auth state and redirect to login
        const { clearUser } = useAuthStore.getState();
        clearUser();

        if (logout) {
          // Preserve current path for post-login redirect
          const currentPath = window.location.pathname + window.location.search;
          const returnUrl = `${window.location.origin}/login${currentPath !== '/' ? `?returnTo=${encodeURIComponent(currentPath)}` : ''}`;

          console.log(
            '@@@ [AuthDebug] Calling Auth0 logout, returnTo:',
            returnUrl
          );
          // Call Auth0 logout - this will clear everything and redirect
          logout({
            logoutParams: {
              returnTo: returnUrl,
            },
          });
        } else {
          // Fallback if logout function not available
          console.warn(
            '@@@ [AuthDebug] No logout function available, redirecting manually'
          );
          const currentPath = window.location.pathname + window.location.search;
          const loginUrl = `/login${currentPath !== '/' ? `?returnTo=${encodeURIComponent(currentPath)}` : ''}`;
          window.location.href = loginUrl;
        }

        return Promise.reject(refreshError);
      }
    }

    // For other errors, just log and reject
    if (error.response?.status === 401) {
      console.error('⚠️ Unauthorized access (no retry):', {
        url: error.config?.url,
        status: error.response?.status,
        retryAttempted: !!originalRequest._retry,
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
